import { FullMetadata } from 'firebase/storage'
import { flatten, uniq } from 'lodash-es'
import {
  CloudAsset,
  CloudAssetStatus,
  IContent,
  IMedia,
  IRegistrationConsent,
  ITemplate,
  ITool,
  ITrait,
  MediaWithUrl,
  MultiLanguageString,
  RegistrationConsentReference,
  TemplateDocs,
  TraitWrapper,
} from 'types'
import { getDocById, getFileMeta, getFileUrl } from 'utils'
import { useExplorerConfig } from '../components/providers'
import { fileExists, fileMeta } from './filesystem'
import { getLayouts } from './layoutUtils'

export async function getDocuments(
  template: ITemplate,
  existingDocs?: TemplateDocs | null
): Promise<TemplateDocs> {
  const consents = await getConsentDocuments(template.registration?.consents)
  const traits = await getTraitDocuments(
    Object.values(template?.traitMap ?? {}),
    existingDocs?.traits
  )
  const media = await getMediaDocuments(template, traits, existingDocs?.media)
  const content = await getContentDocuments(template, existingDocs?.content)
  const tools = await getToolDocuments(template, existingDocs?.tools)

  return {
    consents,
    content,
    media,
    template,
    tools,
    traits: traits.filter(g => g.id),
  }
}

async function getConsentDocuments(consents?: RegistrationConsentReference[]) {
  if (!consents?.length) return []

  const ids = uniq(consents.map(c => c.consentId) || [])
  const docs = await Promise.all(ids.map(id => getDocById('registrationConsents', id)))
  return docs.filter(Boolean) as IRegistrationConsent[]
}

async function getTraitDocuments(wrappers: TraitWrapper[], existingTraitGroups?: ITrait[]) {
  const traitIds = uniq(wrappers.map(o => o.id).filter(Boolean)) as string[]

  const existingTraitGroupIds = new Set(existingTraitGroups?.map(g => g.id) ?? [])
  const newTraitGroupIds = traitIds.filter(id => !existingTraitGroupIds.has(id))

  const newTraitGroups = (
    await Promise.all(newTraitGroupIds.map(id => getDocById('traits2', id)))
  ).filter(Boolean) as ITrait[]

  const traits = wrappers.map(w => {
    const existingGroup = existingTraitGroups?.find(g => g.id === w.id)
    const newGroup = newTraitGroups.find(g => g.id === w.id)
    const group = existingGroup || newGroup
    return { ...group, ...w } as ITrait
  })

  return traits
}

async function getContentDocuments(template: ITemplate, existingContent?: IContent[]) {
  const layouts = getLayouts(template.layoutMap)
  const contentIds = layouts.reduce((acc, cur) => {
    const { action, title } = cur
    if (action?.title) acc = [...acc, ...getIdsFromText(action.title, 'content')]
    if (title) acc = [...acc, ...getIdsFromText(title.text, 'content')]
    if (cur.type === 'text') {
      acc = [...acc, ...getIdsFromText(cur.text, 'content')]
    }
    return acc
  }, [] as string[])

  const existingContentIds = new Set(existingContent?.map(c => c.id) ?? [])
  const newContentIds = contentIds.filter(id => !existingContentIds.has(id))
  const newContent = await Promise.all(newContentIds.map(id => getDocById('content', id)))

  return [...(existingContent ?? []), ...(newContent.filter(Boolean) as IContent[])]
}

function getIdsFromText(text: MultiLanguageString | undefined, type: 'content') {
  if (!text) return []

  const ids = Object.values(text).map((text: string) => {
    const matches = text.match(/\{\{(.+?)\}\}/g)?.map(m => m.slice(2, -2))
    if (!matches) return []

    return matches.reduce((acc, cur) => {
      const [_type, id] = cur.split('|')
      if (_type === type) acc = [...acc, id]
      return acc
    }, [] as string[])
  })

  return uniq(flatten(ids))
}

async function getToolDocuments(template: ITemplate, existingTools?: ITool[]) {
  const layouts = getLayouts(template.layoutMap)
  const toolIds = uniq(
    layouts
      .reduce((acc, cur) => {
        switch (cur.type) {
          case 'tool': {
            acc = [...acc, cur.toolId]
            break
          }
        }
        return acc
      }, [] as string[])
      .filter(Boolean)
  )

  const existingToolIds = new Set(existingTools?.map(t => t.id) ?? [])
  const newToolIds = toolIds.filter(id => !existingToolIds.has(id))

  const newTools = (await Promise.all(newToolIds.map(id => getDocById('tools', id)))) as ITool[]
  return [...(existingTools ?? []), ...newTools]
}

async function getMediaDocuments(
  template: ITemplate,
  traits: ITrait[],
  existingMedia?: MediaWithUrl[]
) {
  const ids = getMediaIds(template, traits)

  const existingMediaIds = new Set(existingMedia?.map(m => m.id) ?? [])
  const newMediaIds = ids.filter(id => !existingMediaIds.has(id))

  const newDocs = await Promise.all(newMediaIds.map(id => getDocById('Media', id)))
  const newMedia = await Promise.all(
    (newDocs.filter(Boolean) as IMedia[]).map(async m => {
      const filePath = `${getWorkspacePath(m.workspaceId)}/${getStoragePath(m)}`
      const url = await getFileUrl(filePath)
      const withUrl: MediaWithUrl = { ...m, downloadUrl: url, url }
      if (m.type !== 'video') return withUrl

      const coverFilePath = `${getWorkspacePath(m.workspaceId)}/${getImagePath(m.id)}`
      const coverUrl = await getFileUrl(coverFilePath)
      return { ...withUrl, coverUrl, coverDownloadUrl: coverUrl }
    })
  )

  return [...(existingMedia ?? []), ...newMedia]
}

export const getWorkspacePath = (workspaceId: string) => `Workspaces/${workspaceId}`
export function getStoragePath(media: IMedia) {
  if (media.type === 'image') return getImagePath(media.id)
  else if (media.type === 'video') return getVideoPath(media.id)
  else if (media.type === 'pdf') return getPDFPath(media.id)
  else return ''
}
export const getImagePath = (mediaId: string) => `media/images/${mediaId}`
export const getVideoPath = (mediaId: string) => `media/videos/${mediaId}`
export const getPDFPath = (mediaId: string) => `media/pdfs/${mediaId}`

function getMediaIds(template: ITemplate, traits: ITrait[]) {
  const { backgroundMedia, logo, screensaverMedia, layoutMap, feedback } = template

  const layoutIds = uniq(
    getLayouts(layoutMap)
      .reduce((acc, cur) => {
        if (cur.type === 'media') acc = [...acc, cur.mediaId]
        return acc
      }, [] as string[])
      .filter(Boolean)
  )

  const traitIds = traits.reduce((acc, cur) => {
    if (cur.media) acc = [...acc, cur.media]
    cur.values.forEach(v => v.media && (acc = [...acc, v.media]))
    return acc
  }, [] as string[])

  const ids = [
    ...(backgroundMedia ? [backgroundMedia] : []),
    ...(feedback?.qrCodeMedia ? [feedback.qrCodeMedia] : []),
    ...(logo?.media ? [logo.media] : []),
    ...(screensaverMedia?.initialImage ? [screensaverMedia.initialImage] : []),
    ...(screensaverMedia?.items?.map(i => i.id) ?? []),
    ...traitIds,
    ...layoutIds,
  ]

  return uniq(ids.filter(Boolean)) as string[]
}

interface FileMeta {
  id: string
  meta: FullMetadata
  filePath: string
  coverMeta?: FullMetadata
  coverFilePath?: string
}

export async function getCloudAssets(media: IMedia[]): Promise<CloudAsset[]> {
  async function getMeta(m: IMedia) {
    const storagePath = getStoragePath(m)
    const cloudPath = `${getWorkspacePath(m.workspaceId)}/${storagePath}`
    const meta = await getFileMeta(cloudPath)
    const fileExtension = getFileExtension(meta.contentType)
    const filePath = `${storagePath}${fileExtension ? `.${fileExtension}` : ''}`
    return { id: m.id, meta, filePath } as FileMeta
  }

  let fileMetas = await Promise.all(
    media.map(async m => {
      const meta = await getMeta(m)
      if (m.type !== 'video') return meta

      const imageMeta = await getMeta({ ...m, type: 'image' })
      return { ...meta, coverMeta: imageMeta.meta, coverFilePath: imageMeta.filePath }
    })
  )

  return Promise.all(
    fileMetas.map(async ({ meta, coverMeta, ...rest }) => {
      let status: CloudAssetStatus = 'unchanged'

      const exists = await fileExists(rest.filePath)
      if (!exists) status = 'new'
      else {
        const local = await fileMeta(rest.filePath)
        if (local.size !== meta.size) status = 'updated' // not sure this is the best way to check if file needs updating
      }

      const { fullPath, size, contentType } = meta
      const downloadUrl = await getFileUrl(fullPath)
      const coverDownloadUrl = coverMeta?.fullPath && (await getFileUrl(coverMeta?.fullPath))
      return { ...rest, contentType, downloadUrl, status, totalBytes: size, coverDownloadUrl }
    })
  )
}

export function getFileExtension(contentType?: string | null) {
  switch (contentType) {
    case 'image/svg+xml':
      return 'svg'
    case 'image/jpeg':
      return 'jpg'
    case 'image/png':
      return 'png'
    case 'video/mp4':
      return 'mp4'
    default:
      return ''
  }
}

export async function allMediaFilesExist(media: MediaWithUrl[]) {
  try {
    await Promise.all(media.map(m => fetch(m.url)))
    return true
  } catch (e) {
    return false
  }
}

function useMediaById() {
  const { templateDocs } = useExplorerConfig()
  return (templateDocs?.media ?? []).reduce(
    (acc, cur) => ({ ...acc, [cur.id]: cur }),
    {} as Record<string, MediaWithUrl>
  )
}

export function useFeedbackQRCode() {
  const { templateDocs } = useExplorerConfig()
  const mediaById = useMediaById()
  const mediaId = templateDocs?.template?.feedback?.qrCodeMedia
  if (!mediaId) return

  return mediaById[mediaId]
}
